import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectors } from '@twaice-fe/frontend/shared/store';
import { filter, map, Observable, tap } from 'rxjs';
import { BessMetadata, ComponentLevelCount, StorageLevel } from '@twaice-fe/shared/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const { systemSelectors } = selectors;

export enum UnitConversion {
  PercentageStorageEnergy = 'PercentageStorageEnergy',
  TemperatureCelsiusFahrenheit = 'TemperatureCelsiusFahrenheit',
}

@Injectable({
  providedIn: 'root',
})
export class UnitConversionService {
  private bessMetadata: BessMetadata | null;
  private componentCountOnLevels: ComponentLevelCount[];
  private bessMetadata$: Observable<BessMetadata | null>;

  constructor(private store: Store) {
    this.bessMetadata$ = this.store.select(systemSelectors.getSelected).pipe(
      filter((system) => !!system),
      takeUntilDestroyed(),
      tap((system) => {
        this.bessMetadata = system.bessMetadata;
        this.componentCountOnLevels = system.componentCountOnLevels;
      }),
      map((system) => system.bessMetadata)
    );
    this.bessMetadata$.subscribe();
  }

  getAvailableConversions$() {
    return this.bessMetadata$.pipe(
      map((metadata) => {
        if (metadata?.installedEnergyCapacityMwh) {
          return [UnitConversion.PercentageStorageEnergy, UnitConversion.TemperatureCelsiusFahrenheit];
        } else {
          return [UnitConversion.TemperatureCelsiusFahrenheit];
        }
      })
    );
  }

  convertValueToUnit(value: number, from: string, to: string, storageLevel: StorageLevel | undefined = undefined): number {
    if (value === undefined || value === null) return value;
    if (from === to) return value;
    if (from === '%' && to === 'MWh') return this.convertPercentageToMegaWattHours(value, storageLevel);
    if (from === 'MWh' && to === '%') return this.convertMegaWattHoursToPercentage(value, storageLevel);
    if (from === 'Wh' && to === '%') return this.convertMegaWattHoursToPercentage(value / 1e6, storageLevel);
    if (from === '°C' && to === '°F') return this.convertCelsiusToFahrenheit(value);
    if (from === '°F' && to === '°C') return this.convertFahrenheitToCelsius(value);
    return value;
  }

  convertValueUsingTransformationFunction(value: number, transformationFunc: (value: number) => number): number {
    return transformationFunc(value);
  }

  private convertCelsiusToFahrenheit(temperatureInCelsius: number): number {
    return (temperatureInCelsius * 9) / 5 + 32;
  }

  private convertFahrenheitToCelsius(temperatureInFahrenheit: number) {
    return ((temperatureInFahrenheit - 32) * 5) / 9;
  }

  private convertPercentageToMegaWattHours(pctValue: number, storageLevel: StorageLevel | undefined = undefined): number {
    const megaWattHoursValue = (pctValue / 100) * (this.bessMetadata?.installedEnergyCapacityMwh ?? 1);
    if (storageLevel) {
      return megaWattHoursValue / (this.getNumberOfComponentsOnLevel(storageLevel) ?? 1);
    } else {
      return megaWattHoursValue;
    }
  }

  private convertMegaWattHoursToPercentage(megaWattHoursValue: number, storageLevel: StorageLevel | undefined = undefined) {
    const pctValue = (megaWattHoursValue / (this.bessMetadata?.installedEnergyCapacityMwh ?? 1)) * 100;
    if (storageLevel) {
      return pctValue * (this.getNumberOfComponentsOnLevel(storageLevel) ?? 1);
    } else {
      return pctValue;
    }
  }

  private getNumberOfComponentsOnLevel(level: StorageLevel) {
    const levelInfo = (this.componentCountOnLevels ?? []).find((containerInfo) => containerInfo.levelBk === level);
    if (!levelInfo) return null;

    return levelInfo.count;
  }
}
